export interface ScoreRange {
  min: number
  max: number
}

export interface ProfileScoring {
  identity: ScoreRange
  community: ScoreRange
  purpose: ScoreRange
}

export interface ProfileStrength {
  dimension: "identity" | "community" | "purpose"
  level: "strong" | "growing" | "emerging"
}

export interface BiblicalExample {
  name: string
  role: string
  description: RichTextField
}

export interface GrowthInvitation {
  title: string
  description: string
}

export interface KeyModel {
  name: string
  content: string | PortableTextContent
}

/** Portable text block array from Sanity rich text fields. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type PortableTextContent = Array<{ _type: string; [key: string]: any }>

/** A field that can be plain text (fallback), string[] (legacy currentState), or PortableText. */
export type RichTextField = string | string[] | PortableTextContent

export interface Profile {
  id: string
  name: string
  tagline: string
  openingQuote: RichTextField
  summary: string
  strengths: ProfileStrength[]
  keyModel: KeyModel
  affirmation: RichTextField
  reality: RichTextField
  biblicalExamples: BiblicalExample[]
  currentState: RichTextField
  goodNews: RichTextField
  invitationIntro: RichTextField
  growthInvitations: GrowthInvitation[]
  closingQuote: string
  closingVerse: string
}

export interface SectionLabels {
  coreProfile: string
  coreSnapshot: string
  keyModel: string
  affirmation: string
  reality: string
  biblicalExamples: string
  biblicalExample: string
  currentState: string
  goodNews: string
  invitationToMore: string
  whichProfile: string
  noWrongAnswers: string
}

export const SECTION_LABELS: SectionLabels = {
  coreProfile: "YOUR CORE PROFILE",
  coreSnapshot: "YOUR CORE SNAPSHOT",
  keyModel: "THE MODEL: JESUS",
  affirmation: "AFFIRMATION",
  reality: "REALITY",
  biblicalExamples: "BIBLICAL EXAMPLES OF THE TENSION",
  biblicalExample: "BIBLICAL EXAMPLE",
  currentState: "YOUR CURRENT STATE & FRUSTRATIONS",
  goodNews: "HERE IS THE GOOD NEWS",
  invitationToMore: "AN INVITATION TO MORE",
  whichProfile: "WHICH PROFILE RESONATES WITH YOU?",
  noWrongAnswers: "NO WRONG ANSWERS",
} as const

export interface ProfilePageContent {
  profile: Profile
  labels: SectionLabels
  multipleProfileMessage: string
}

const defaultKeyModel: KeyModel = {
  name: "Jesus",
  content:
    "Jesus is the only man who lived fully aligned in His IDENTITY, with His COMMUNITY, and in His PURPOSE.\n\n\u2022 His Identity was unshakable. \u201CThis is my beloved Son\u2026\u201D [Matthew 3:17]. He never performed for approval.\n\u2022 His Community was deep and intentional. He built real friendships with twelve, invested purposefully in three, and was always present with everyone He met, fully known and fully engaged. [John 13:1]\n\u2022 His Purpose was clear and costly. \u201CI have come to do the will of Him who sent me\u201D [John 6:38]. Every step toward the cross was on mission, on time, and without distraction.\n\nOur Call: We are not Jesus, but we are being conformed to His likeness. The goal is to be grounded in identity, connected in community, and clear in purpose. Maturity is growing into alignment with Christ\u2014becoming by grace what He is by nature.",
}

export const multipleProfileMessage =
  "Your feedback shows a strong connection to two profiles. Take a moment to review both and choose the one that resonates most with you right now. Remember\u2014this is a SNAPSHOT of where you are today, not a final definition of who you are."

export const profiles: Profile[] = [
  {
    id: "connected",
    name: "Connected",
    tagline: "Strong in Community \u00B7 Growing in Identity",
    openingQuote:
      "\u201CConfusion arises from conflicting narratives about masculinity and societal roles. Men need an understanding of biblical manhood rooted in Christ\u2019s likeness that transcends all other identities\u2026\u201D \u2014Dr. A.R. Bernard",
    summary:
      "Because you have a strong sense of community, but your identity is less developed, you are likely active, engaged, and relationally central. Your identity may fluctuate based on approval or social feedback.",
    strengths: [
      { dimension: "community", level: "strong" },
      { dimension: "purpose", level: "growing" },
      { dimension: "identity", level: "emerging" },
    ],
    keyModel: defaultKeyModel,
    affirmation:
      "You are relationally strong:\n\n\u2022 You value people and invest deeply in relationships.\n\u2022 You show up, care, and create spaces where others feel seen and supported.\n\u2022 You are not doing life alone\u2014and that matters.",
    reality:
      "Community is powerful, but it cannot define you:\n\n\u2022 Your identity must rest on whose you are, not who you\u2019re with [Galatians 2:20].\n\u2022 Without identity as a foundation, relationships can become controlling or create dependency.\n\u2022 Community is strongest when identity is clear and secure.",
    biblicalExamples: [
      {
        name: "Aaron",
        role: "Priest \u00B7 Relationally Connected \u00B7 Internally Unsteady",
        description:
          "Aaron shows what it looks like to be connected but unsteady. He leads the people into building a golden calf, swayed by pressure from those around him [Exodus 32]. When identity is weak, community shapes you. Yet God is not done\u2014God reminds Aaron that he is a priest, an identity he received, backed by the promises of God.",
      },
      {
        name: "Abraham",
        role: "Patriarch \u00B7 Influential Presence \u00B7 Fear-Driven Identity",
        description:
          "Abraham stood among people, influential and surrounded\u2014yet when pressure rose, fear spoke louder than truth. Twice, he called Sarah his sister, choosing self-protection over trust [Genesis 12:11\u201313; 20:2]. His relationships were real, but his identity was not yet rooted. So God met him there, not with condemnation, but with clarity: \u201CDo not be afraid\u2026 I am your shield\u201D [Gen 15:1]. Before Abraham could stand firm before others, he had to be secured in who he was before God.",
      },
    ],
    currentState: [
      "You are likely active, engaged, and relationally central.",
      "Your identity may fluctuate based on approval or social feedback.",
      "You may adapt to others, avoid conflict, or confuse being liked with being rooted.",
    ],
    goodNews:
      "None of this defines who you are. Rooting yourself in the truth of what the Scriptures say about who and whose you are today leads to a confident, connected, and complete tomorrow.",
    invitationIntro:
      "If we\u2019re going to go deeper, we need to be grounded. Your identity is formed in Christ first, then strengthened in the presence of others.",
    growthInvitations: [
      {
        title: "Time Alone with God",
        description: "Spend time alone with God, away from external voices.",
      },
      {
        title: "Scripture-Defined Identity",
        description: "Let Scripture\u2014not social feedback\u2014define you.",
      },
      {
        title: "Hold Your Convictions",
        description: "Be ready to say \u201Cno\u201D when your convictions could be compromised.",
      },
      {
        title: "Speak Honestly",
        description: "Speak honestly, even at the risk of approval.",
      },
      {
        title: "Invite Rooted Men In",
        description:
          "Invite those in your community who are rooted in their identity to help you see and grow in your own.",
      },
    ],
    closingQuote:
      "Community is strongest when it flows from a man who knows who he is. Be that man\u2014rooted in identity, fully present in relationships, and aligned with purpose. That\u2019s the path to maturity, real influence, and a lasting, God-honoring impact.",
    closingVerse:
      "You are accepted in Christ before you are affirmed by people.",
  },
  {
    id: "bonded",
    name: "Bonded",
    tagline: "Strong in Community \u00B7 Growing in Purpose",
    openingQuote:
      "\u201COur discipleship models, with the church and the post-enlightenment era, have been very content-driven. That is not primarily what men are looking for. It needs to be primarily action-driven.\u201D \u2014John Eldredge",
    summary:
      "Because you have a strong sense of community, and your purpose is less developed, you probably say \u201Cyes\u201D too often, overcommit, and help others move forward while you feel stuck.",
    strengths: [
      { dimension: "community", level: "strong" },
      { dimension: "identity", level: "growing" },
      { dimension: "purpose", level: "emerging" },
    ],
    keyModel: defaultKeyModel,
    affirmation:
      "You are relationally engaged:\n\n\u2022 You consistently show up for people\u2014you are actively present.\n\u2022 Others know they can count on you\u2014this kind of availability is rare.\n\u2022 You bring encouragement, consistency, and care into the lives of those around you.",
    reality:
      "Community is a gift, but it cannot replace calling:\n\n\u2022 You weren\u2019t just created to be with people; you were created to be sent [John 20:21].\n\u2022 When your community isn\u2019t connected to purpose, it can drift. What should produce impact can produce passivity.\n\u2022 Community without purpose can lead to comfort over calling.",
    biblicalExamples: [
      {
        name: "The Disciples (Pre-Acts)",
        role: "Followers of Jesus \u00B7 Close Proximity \u00B7 Slow Alignment",
        description:
          "They walked with Jesus, saw the miracles, and lived in tight community\u2014yet their hearts lagged behind. Jesus called them \u201Cfoolish\u201D and \u201Cslow to believe\u201D [Luke 24:25]. Proximity didn\u2019t equal purpose. So He clarified the mission\u2014\u201CYou will be my witnesses\u2026\u201D [Acts 1:8]\u2014and when the Spirit came [Acts 2:1\u20134], their community became a movement.",
      },
      {
        name: "The Early Church",
        role: "Believers \u00B7 Strong Fellowship \u00B7 Potential Drift",
        description:
          "They were devoted, unified, and deeply connected [Acts 2:42\u201347]. It was powerful\u2014but it risked turning inward. So God allowed disruption. Scattered by persecution, they didn\u2019t lose community\u2014they multiplied it. What was gathered was sent [Acts 8:1, 4].",
      },
    ],
    currentState: [
      "You probably say \u201Cyes\u201D too often, overcommit, and help others move forward while you feel stuck.",
      "You can easily mistake being present for being intentional.",
      "Your relational engagement is high, but your directional clarity may be low.",
    ],
    goodNews:
      "None of this is permanent. Clarifying your purpose today leads to a more focused, connected, and aligned tomorrow.",
    invitationIntro:
      "If we\u2019re going to go deeper, we need clarity. Purpose is not found in constant availability; it\u2019s discovered through focused obedience.",
    growthInvitations: [
      {
        title: "Define Your Calling",
        description: "Define what God is specifically calling you to build and pursue.",
      },
      {
        title: "Prioritize Mission",
        description: "Prioritize mission over mere activity.",
      },
      {
        title: "Align Relationships",
        description: "Align your relationships with your calling.",
      },
      {
        title: "Own Your Time",
        description: "Take more ownership of your time and energy.",
      },
      {
        title: "Ask the Right Question",
        description:
          "Ask not just, \u201CWho needs me?\u201D but \u201CWhere am I being sent?\u201D",
      },
    ],
    closingQuote:
      "Community thrives when it flows from a man who knows his purpose and shows up with intention. Be that man\u2014engaged, present, and leading in a way that strengthens and connects others. That\u2019s the path to maturity, real influence, and lasting impact.",
    closingVerse:
      "Community doesn\u2019t grow from mere presence; it\u2019s built by men who show up with purpose together.",
  },
  {
    id: "anchored",
    name: "Anchored",
    tagline: "Strong in Identity \u00B7 Growing in Community",
    openingQuote:
      "\u201C\u2026community is first of all a quality of the heart. It grows from the spiritual knowledge that we are alive not for ourselves but for one another. Community is the fruit of our capacity to make the interests of others more important than our own.\u201D \u2014Henri Nouwen",
    summary:
      "Because you have a strong sense of identity, but your community is less developed, you may be secure in Christ but might lack deep and consistent brotherhood.",
    strengths: [
      { dimension: "identity", level: "strong" },
      { dimension: "purpose", level: "growing" },
      { dimension: "community", level: "emerging" },
    ],
    keyModel: defaultKeyModel,
    affirmation:
      "You are grounded in who you are:\n\n\u2022 Opinions, pressure, or comparison do not easily shake you.\n\u2022 You likely walk with a quiet confidence in Christ that isn\u2019t performative\u2014it\u2019s real.\n\u2022 You have a strong sense of who you are in Christ.",
    reality:
      "Identity is personal, but it was never meant to be private:\n\n\u2022 You weren\u2019t just adopted into Christ\u2014you were adopted into a family [Ephesians 2:19].\n\u2022 When identity doesn\u2019t move into community, it can begin to isolate. What should produce brotherhood may start to create distance.\n\u2022 Identity matures in the context of others\u2014sharpened, strengthened, and lived out shoulder to shoulder [Proverbs 27:17].",
    biblicalExamples: [
      {
        name: "Elijah",
        role: "Prophet \u00B7 Strong Identity \u00B7 Isolated Soul",
        description:
          "Elijah stood boldly before kings and called down fire from heaven, yet in the quiet, he collapsed under the weight of loneliness. \u201CI, even I only, am left\u2026\u201D he cried [1 Kings 19:10, 14]. Powerful in public, isolated in private. So God met him, not just with provision, but with perspective. \u201CYou are not alone.\u201D He revealed a remnant and reconnected Elijah to people [1 Kings 19:18\u201321]. Because even strong men aren\u2019t meant to stand alone.",
      },
      {
        name: "Joseph",
        role: "Patriarch \u00B7 Secure Identity \u00B7 Relational Separation",
        description:
          "Joseph walked with God in the pit, the prison, and the palace. \u201CThe Lord was with Joseph\u2026\u201D [Genesis 39:2\u20133]. His identity was steady, even when his relationships were stripped away. So God used the isolation, not to break him, but to form him. And in time, He restored Joseph, not just to power, but to people. From separation to influence, from hidden to positioned\u2014God worked in Joseph for the sake of many.",
      },
    ],
    currentState: [
      "You may be secure in Christ, but might lack deep and consistent brotherhood.",
      "Your relationships may be limited, surface-level, or inconsistent.",
      "You may confuse maturity with independence or self-sufficiency.",
    ],
    goodNews:
      "None of this is permanent. Taking a step toward others today leads to a more connected, confident, and complete tomorrow.",
    invitationIntro:
      "If you want to go deeper, you have to get connected. Brotherhood doesn\u2019t happen by accident\u2014it\u2019s built on purpose.",
    growthInvitations: [
      {
        title: "Pursue Friendship",
        description:
          "Identify 2\u20133 men and pursue real friendship\u2014don\u2019t wait for it to happen.",
      },
      {
        title: "Invite Them In",
        description:
          "Invite them into your actual life\u2014the hopes, the struggles, the pressures, the real stuff.",
      },
      {
        title: "Set a Rhythm",
        description: "Set a weekly rhythm\u2014show up consistently, face to face.",
      },
      {
        title: "Ask for Truth",
        description:
          "Ask for truth, prayer, and accountability\u2014and receive it without defensiveness.",
      },
      {
        title: "Be Fully Known",
        description: "Choose to be fully known, not just casually familiar.",
      },
    ],
    closingQuote:
      "Identity isn\u2019t just something you hold\u2014it\u2019s something you live out, shoulder to shoulder with other men. Be a man who is secure in who he is and willing to step into deep, sharpening relationships that challenge, encourage, and refine him. That\u2019s the path to maturity, real connection, and lasting impact.",
    closingVerse:
      "Identity grows stronger when it\u2019s lived out in brotherhood, not kept in isolation.",
  },
  {
    id: "rooted",
    name: "Rooted",
    tagline: "Strong in Identity \u00B7 Growing in Purpose",
    openingQuote:
      "\u201CWhen we really understand our identity in Christ, we will exercise our authority in Christ.\u201D \u2014Kurt Sauder",
    summary:
      "Because you have a strong sense of identity, but your purpose is less developed, you may feel secure in Christ but unclear in calling.",
    strengths: [
      { dimension: "identity", level: "strong" },
      { dimension: "community", level: "growing" },
      { dimension: "purpose", level: "emerging" },
    ],
    keyModel: defaultKeyModel,
    affirmation:
      "You are grounded in who you are:\n\n\u2022 You likely have a strong sense of your true identity.\n\u2022 You are rooted in Christ, not striving for approval but living from it.\n\u2022 You have a settled confidence that many men never find.",
    reality:
      "Identity is the foundation, but it was never meant to be the finish line.\n\n\u2022 You weren\u2019t just saved from something; you were saved for something [Ephesians 2:10].\n\u2022 When identity doesn\u2019t move into purpose, it can stagnate. What should create movement may lead to apathy.\n\u2022 Purpose is where identity proves itself\u2014lived out through action, obedience, and impact.",
    biblicalExamples: [
      {
        name: "Jonah",
        role: "Prophet \u00B7 Clear Identity \u00B7 Resisted Mission",
        description:
          "Jonah knew who he was\u2014a prophet of God. But when the call came, he ran [Jonah 1:1\u20133]. Identity was clear; obedience was not. So God pursued him. Through discipline, mercy, and a second calling, \u201CThe word of the Lord came\u2026 a second time\u201D [Jonah 3:1\u20132], God redirected Jonah back to purpose. God doesn\u2019t just define you, He guides you every step of the way [Proverbs 16:9].",
      },
      {
        name: "Habakkuk",
        role: "Prophet \u00B7 Clear Identity \u00B7 Questioning Purpose",
        description:
          "Habakkuk knew who he was\u2014a prophet who stood before God and spoke honestly with Him. He didn\u2019t question his relationship with God, but he wrestled with what God was doing: \u201CO Lord, how long shall I cry for help, and you will not hear?\u201D [Habakkuk 1:2]. Even when God answered, the plan confused him [Habakkuk 1:5\u201313]. Identity was secure; understanding his role in the midst of it was not. But Habakkuk stayed engaged, choosing to stand and wait for clarity (Habakkuk 2:1). In the end, his confidence wasn\u2019t in the outcome, but in God Himself: \u201CYet I will rejoice in the Lord\u201D [Habakkuk 3:17\u201318]. God doesn\u2019t just give you a place\u2014He invites you to trust Him when the purpose isn\u2019t fully clear.",
      },
    ],
    currentState: [
      "You may feel secure in Christ but unclear in calling.",
      "You might think deeply but act slowly.",
      "You may consume more than you create, waiting for clarity instead of stepping into obedience.",
    ],
    goodNews:
      "None of this is permanent. Stepping into motion today leads to a more focused, confident, and complete tomorrow.",
    invitationIntro:
      "If we\u2019re going to go deeper, we need motion. Purpose is rarely discovered in isolation; it\u2019s revealed in obedience.",
    growthInvitations: [
      {
        title: "Take Responsibility",
        description: "Take responsibility for something that costs you.",
      },
      {
        title: "Engage Your Faith",
        description:
          "Engage your faith in real places: your home, your work, your church.",
      },
      {
        title: "Act on Conviction",
        description:
          "Stop waiting for perfect clarity\u2014act on present conviction.",
      },
      {
        title: "Embody Your Faith",
        description: "Trade reflection-only faith for active, embodied faith.",
      },
      {
        title: "Ask Where You\u2019re Sent",
        description:
          "Ask not just, \u201CWho am I?\u201D but \u201CWhere am I sent?\u201D",
      },
    ],
    closingQuote:
      "Identity reaches its fullest expression when it\u2019s lived, not just understood. Be a man who is secure in who he is\u2014and takes decisive steps to walk in his calling. That\u2019s the path to maturity, purpose, and lasting impact.",
    closingVerse:
      "Clarity of identity means little if it never becomes action.",
  },
  {
    id: "focused",
    name: "Focused",
    tagline: "Strong in Purpose \u00B7 Growing in Community",
    openingQuote:
      "\u201COur compassion for others is the measure of our greatness.\u201D \u2014Paul Cole",
    summary:
      "Because you have a strong sense of purpose, but your community is less developed, relationships may feel functional rather than fulfilling.",
    strengths: [
      { dimension: "purpose", level: "strong" },
      { dimension: "identity", level: "growing" },
      { dimension: "community", level: "emerging" },
    ],
    keyModel: defaultKeyModel,
    affirmation:
      "You are driven and on mission:\n\n\u2022 You probably don\u2019t waste a lot of time\u2014you move with intention.\n\u2022 You carry weight, take responsibility, and move things forward.\n\u2022 You likely demonstrate a level of focus and execution that many men never reach.",
    reality:
      "Purpose was never meant to be carried alone.\n\n\u2022 You weren\u2019t called to run solo; you were placed in a body [Ecclesiastes 4:9\u201310].\n\u2022 When purpose isn\u2019t connected to community, it can isolate. What should create impact may lead to burnout.\n\u2022 Shared purpose multiplies strength\u2014what you carry with others lasts longer, goes further, and bears more fruit.",
    biblicalExamples: [
      {
        name: "Samson",
        role: "Judge \u00B7 Powerful Calling \u00B7 Isolated Living",
        description:
          "Samson was set apart, filled with strength, and marked by God for a purpose, yet he chose to walk alone. He rejected counsel, resisted community, and leaned on his own power [Judges 14\u201316]. And in the end, his strength became his weakness. Only in his final moment did he cry out to God, and God still used him [Judges 16:28\u201330]. But the cost was high. Because strength without brotherhood doesn\u2019t protect you\u2014it exposes you.",
      },
      {
        name: "Moses",
        role: "Prophet \u00B7 Clear Purpose \u00B7 Isolated Leadership",
        description:
          "Moses knew what he was called to do\u2014to lead Israel out of Egypt and into freedom [Exodus 3:10]. He carried a clear sense of purpose and bore it with resolve, confronting Pharaoh and guiding the people through the wilderness. Purpose was strong; community was strained. He often stood alone, overwhelmed by the weight of leadership: \u201CI cannot carry all these people by myself; the burden is too heavy for me\u201D [Numbers 11:14]. Even his father-in-law saw the isolation and warned him, \u201CWhat you are doing is not good\u2026 you will certainly wear yourself out\u201D [Exodus 18:17\u201318]. So God provided support through others, establishing shared leadership and reminding Moses he wasn\u2019t meant to carry it alone [Exodus 18:21\u201323]. God doesn\u2019t just give you a calling\u2014He surrounds you with people to sustain it.",
      },
    ],
    currentState: [
      "Relationships may feel functional rather than fulfilling.",
      "You may see people as interruptions rather than investments.",
      "You may be widely respected, but truly known by few.",
    ],
    goodNews:
      "None of this is irreversible. Leaning into brotherhood today leads to a more connected, focused, and complete tomorrow.",
    invitationIntro:
      "If we\u2019re going to go deeper, we need to be connected. Community is not a distraction from your mission\u2014it\u2019s essential to it.",
    growthInvitations: [
      {
        title: "Invite Men In",
        description: "Invite men into your activity and your life.",
      },
      {
        title: "Share the Weight",
        description:
          "Share what you\u2019re carrying\u2014don\u2019t just manage it.",
      },
      {
        title: "Create Connection Rhythms",
        description:
          "Create consistent rhythms for connection, not just output.",
      },
      {
        title: "Let Others Support You",
        description: "Let others encourage and support you.",
      },
      {
        title: "Measure by Relationships",
        description:
          "Measure success not just by what you build, but by who you build with.",
      },
    ],
    closingQuote:
      "Purpose goes further and lasts longer when it\u2019s carried with other men. Be a man who leads with clarity, builds with others, and refuses to carry what was meant to be shared. Cultivate trusted relationships as you move\u2014because that\u2019s where strength multiplies. That\u2019s the path to maturity, brotherhood, and lasting impact.",
    closingVerse:
      "You can be on mission and still be alone. Biblically, purpose without community is not strength\u2014it\u2019s a slow leak. Lone wolves burn out, brotherhood builds what lasts.",
  },
  {
    id: "motivated",
    name: "Motivated",
    tagline: "Strong in Purpose \u00B7 Growing in Identity",
    openingQuote:
      "\u201CI went through so much of my life listening to culture about who I should be rather than who I am.\u201D \u2014Justin Camp",
    summary:
      "Because you have a strong sense of purpose, but your identity is less developed, you might tie your value to productivity or performance.",
    strengths: [
      { dimension: "purpose", level: "strong" },
      { dimension: "community", level: "growing" },
      { dimension: "identity", level: "emerging" },
    ],
    keyModel: defaultKeyModel,
    affirmation:
      "You are driven and motivated:\n\n\u2022 You want your life to count and carry a sense that you were made for more.\n\u2022 You are not content to sit on the sidelines\u2014you move, build, and initiate.\n\u2022 There is a fire in you that many men lack.",
    reality:
      "Purpose is powerful, but it was never meant to define you. You can be doing a lot for God and still not be rooted in Him. Biblically, activity without identity leads to burnout, insecurity, and/or collapse.\n\n\u2022 You don\u2019t earn your identity through what you do; you live from who you are [Luke 10:20].\n\u2022 When purpose isn\u2019t anchored in identity, it becomes exhausting. What should produce impact can begin to produce despondency and resignation.\n\u2022 Identity steadies your purpose\u2014when you know who you are, you can lead, serve, and build without striving for approval.",
    biblicalExamples: [
      {
        name: "Saul",
        role: "King \u00B7 Assigned Purpose \u00B7 Insecure Identity",
        description:
          "Saul was chosen, anointed, and appointed king, yet when the moment came, he hid among the baggage [1 Samuel 10:22]. The position was clear, but his identity was not. And when identity is weak, purpose feels like pressure. God confronted him, \u201CThough you are little in your own eyes\u2026\u201D [1 Samuel 15:17], but Saul kept reaching for the approval of people over the approval of God [1 Samuel 15:24]. When you don\u2019t know who you are, you\u2019ll let others define you.",
      },
      {
        name: "The Older Brother",
        role: "Son \u00B7 Dutiful Living \u00B7 Distorted Identity",
        description:
          "He stayed. He served. He did everything right. And yet when grace was given to his younger brother, bitterness rose up: \u201CAll these years I\u2019ve served you\u2026\u201D [Luke 15:28\u201330]. His obedience was real, but his identity was off. So the Father met him with truth: \u201CSon, you are always with me, and all that is mine is yours\u201D [Luke 15:31]. Not performance, but identity, because you can do all the right things and still miss who you are.",
      },
    ],
    currentState: [
      "You might tie your value to productivity or performance.",
      "You might chase wins that never fully satisfy.",
      "You may compare yourself to others more than you realize.",
    ],
    goodNews:
      "None of this defines who you are. Rooting your identity today leads to a more confident, focused, and complete tomorrow.",
    invitationIntro:
      "If we\u2019re going to go deeper, we need to know who you are. Identity is not built through achievement; it\u2019s received through abiding.",
    growthInvitations: [
      {
        title: "Unhurried Time with God",
        description:
          "Spend unhurried time with God where nothing is being produced.",
      },
      {
        title: "Rehearse the Truth",
        description:
          "Rehearse the truth: you are a son before you are a servant.",
      },
      {
        title: "Detach Your Worth",
        description: "Detach your worth from your wins and losses.",
      },
      {
        title: "Let Scripture Define You",
        description:
          "Let Scripture\u2014not success\u2014define who you are.",
      },
      {
        title: "Be with God",
        description: "Practice being with God, not just doing for God.",
      },
    ],
    closingQuote:
      "Purpose isn\u2019t sustained by pressure; it\u2019s sustained by a man who knows who he is and no longer feels the need to prove it. Be a man who moves, builds, and leads from a place of secure identity, not striving for approval but living from it. That\u2019s the path to maturity, freedom, and lasting impact.",
    closingVerse:
      "When identity is secure, purpose becomes a calling, not a burden.",
  },
]

/**
 * Determines which profile best matches a user's scores using relative comparison.
 * Maps the highest-scoring dimension against the lowest-scoring dimension.
 * Each category score ranges from 0 to 40 (10 questions x max 4 points each).
 */
const profileMap: Record<string, string> = {
  "community>identity": "connected",
  "community>purpose": "bonded",
  "identity>community": "anchored",
  "identity>purpose": "rooted",
  "purpose>community": "focused",
  "purpose>identity": "motivated",
}

/**
 * Determines which profile best matches a user's scores using relative comparison.
 * Maps the highest-scoring dimension against the lowest-scoring dimension.
 * Each category score ranges from 0 to 40 (10 questions x max 4 points each).
 * Accepts an optional profileList to use instead of the hardcoded profiles array.
 */
export function getMatchingProfile(
  scores: { identity: number; community: number; purpose: number },
  profileList: Profile[] = profiles,
): Profile {
  const dims: { name: string; score: number }[] = [
    { name: "identity", score: scores.identity },
    { name: "community", score: scores.community },
    { name: "purpose", score: scores.purpose },
  ]

  dims.sort((a, b) => b.score - a.score)

  const highest = dims[0].name
  const lowest = dims[2].name

  const key = `${highest}>${lowest}`
  const profileId = profileMap[key]

  if (profileId) {
    return profileList.find((p) => p.id === profileId)!
  }

  return profileList[0]
}

export interface MatchingProfilesResult {
  primary: Profile
  secondary: Profile | null
  isDualProfile: boolean
}

/**
 * Returns primary and optionally secondary profile when scores are close.
 * If the gap between the top two dimensions is <= DUAL_PROFILE_THRESHOLD,
 * both possible profiles are returned.
 * Accepts an optional profileList to use instead of the hardcoded profiles array.
 */
export function getMatchingProfiles(
  scores: { identity: number; community: number; purpose: number },
  forceDual?: boolean,
  profileList: Profile[] = profiles,
): MatchingProfilesResult {
  const dims: { name: string; score: number }[] = [
    { name: "identity", score: scores.identity },
    { name: "community", score: scores.community },
    { name: "purpose", score: scores.purpose },
  ]

  dims.sort((a, b) => b.score - a.score)

  const highest = dims[0]
  const second = dims[1]
  const lowest = dims[2]

  function resolveProfile(high: string, low: string): Profile | null {
    const profileId = profileMap[`${high}>${low}`]
    return profileId
      ? profileList.find((p) => p.id === profileId) ?? null
      : null
  }

  const primary = resolveProfile(highest.name, lowest.name) ?? profileList[0]

  const topGap = highest.score - second.score
  const bottomGap = second.score - lowest.score

  let secondary: Profile | null = null

  if (forceDual || topGap === 0) {
    secondary = resolveProfile(second.name, lowest.name)
  } else if (bottomGap === 0) {
    secondary = resolveProfile(highest.name, second.name)
  }

  if (secondary && secondary.id !== primary.id) {
    return { primary, secondary, isDualProfile: true }
  }

  return { primary, secondary: null, isDualProfile: false }
}
